import { customAlertStateAtom } from "@/components/custom-alert";
import VisuallyHiddenInput from "@/components/visually-hidden-input";
import { checkOAuthStatus, customFirestore, customStorage } from "@/firebase";
import { companyDataAtom } from "@/routes/test-run/test-run-atoms";
import { type CompanyData } from "@/utils/types";
import { Check, CloudUpload, Delete } from "@mui/icons-material";
import { Button, CircularProgress } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { doc, updateDoc, writeBatch } from "firebase/firestore";
import {
  deleteObject,
  ref,
  uploadBytes,
} from "firebase/storage";
import { useSetAtom } from "jotai";
import React, { useContext } from "react";
import { openOAuthPopup } from "@/routes/settings/integrations/oauth";
import { AuthContext } from "@/auth/AuthContext";

export const FirebaseAppDistributionIntegration = ({
  companyData,
}: {
  companyData: CompanyData;
}) => {
  const setCustomAlertState = useSetAtom(customAlertStateAtom);
  const queryClient = useQueryClient();
  const useAuth = () => useContext<any>(AuthContext);
  const { user } = useAuth();

  const deleteFileMutation = useMutation({
    mutationFn: async () => {
      const filePath = companyData.settings.googleOAuthFilePath;
      const storageRef = ref(customStorage, filePath);
      await deleteObject(storageRef);

      const settingsRef = doc(
        customFirestore,
        "settings",
        companyData.organisationId
      );

      const batch = writeBatch(customFirestore);
      batch.update(settingsRef, {
        googleOAuthFilePath: null,
      });
      batch.update(doc(customFirestore, "users", user.uid), {
        googleOAuthToken: null,
      });

      await batch.commit();

      queryClient.setQueryData(
        ["getCompanyData", companyData.organisationId],
        (oldData: CompanyData) => ({
          ...oldData,
          settings: {
            ...oldData.settings,
            googleOAuthFilePath: undefined,
          },
        })
      );
      companyDataAtom.remove(companyData.organisationId);
      queryClient.setQueryData(["oauth-status", user.uid], {
        authorized: false,
        clientId: null,
      });
    },
    onSuccess: () => {
      setCustomAlertState({
        open: true,
        type: "success",
        title: "OAuth file deleted successfully",
      });
    },
    onError: () => {
      setCustomAlertState({
        open: true,
        type: "error",
        title: "Failed to delete OAuth file",
      });
    },
  });

  const uploadFileMutation = useMutation({
    mutationFn: async (file: File) => {
      const content = await file.text();
      const config = JSON.parse(content);

      if (!config.web?.client_id) {
        throw new Error("Invalid OAuth file");
      }

      const storageRef = ref(
        customStorage,
        `google-oauth-files/${companyData.organisationId}/${file.name}`
      );
      await uploadBytes(storageRef, file);

      const settingsRef = doc(
        customFirestore,
        "settings",
        companyData.organisationId
      );
      await updateDoc(settingsRef, {
        googleOAuthFilePath: storageRef.fullPath,
      });

      queryClient.setQueryData(
        ["getCompanyData", companyData.organisationId],
        (oldData: CompanyData) => ({
          ...oldData,
          settings: {
            ...oldData.settings,
            googleOAuthFilePath: storageRef.fullPath,
          },
        })
      );
      companyDataAtom.remove(companyData.organisationId);
      queryClient.setQueryData(["oauth-status", user.uid], {
        authorized: false,
        clientId: config.web.client_id,
      });
    },
    onSuccess: (newClientId) => {
      setCustomAlertState({
        open: true,
        type: "success",
        title: "OAuth file uploaded successfully",
      });
    },
    onError: (error: any) => {
      setCustomAlertState({
        open: true,
        type: "error",
        title: "Upload failed",
        description: error.message,
      });
    },
  });

  const handleFileSelect = (fileList: FileList | null) => {
    const file = fileList?.[0];
    if (!file) return;

    if (file.size > 1024 * 1024) {
      setCustomAlertState({
        open: true,
        type: "error",
        title: "File too large",
        description: "File size should be less than 1MB",
      });
      return;
    }

    if (file.type !== "application/json") {
      setCustomAlertState({
        open: true,
        type: "error",
        title: "Invalid file type",
        description: "Only JSON files are allowed",
      });
      return;
    }

    uploadFileMutation.mutate(file);
  };

  const handleAuthorize = (clientId: string) => {
    openOAuthPopup(
      clientId,
      () => {
        setCustomAlertState({
          open: true,
          type: "success",
          title: "Authorization successful",
        });
        void queryClient.invalidateQueries({ queryKey: ["oauth-status"] });
      },
      (error) => {
        setCustomAlertState({
          open: true,
          type: "error",
          title: "Something went wrong",
          description: error,
        });
      }
    );
  };

  const hasFile = !!companyData.settings.googleOAuthFilePath;

  const oauthQuery = useQuery({
    queryKey: ["oauth-status", user.uid],
    queryFn: async () => {
      const response = await checkOAuthStatus();
      return response.data;
    },
    refetchOnWindowFocus: "always",
  });

  return (
    <>
      <div className="flex gap-1 items-center">
        <div className="flex flex-col gap-1">
          <p className="text-xl">Firebase App Distribution</p>
          <p className="text-sm text-gray-600">
            Upload your Google Cloud OAuth credentials to enable access to App
            Distribution builds.
          </p>
        </div>
      </div>

      <div className="flex flex-col max-w-md gap-4">
        <div className="flex gap-4 items-center">
          <Button
            component="label"
            variant="contained"
            startIcon={<CloudUpload />}
            disabled={hasFile || uploadFileMutation.isPending}
          >
            {uploadFileMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              "Upload OAuth File"
            )}
            <VisuallyHiddenInput
              type="file"
              accept=".json"
              onChange={(e) => handleFileSelect(e.target.files)}
            />
          </Button>

          {hasFile && (
            <>
              <Check className="text-green-500" />
              <span className="text-sm text-gray-600">OAuth file uploaded</span>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<Delete />}
                disabled={deleteFileMutation.isPending}
                onClick={() => deleteFileMutation.mutate()}
              >
                Delete
              </Button>
            </>
          )}
        </div>

        {hasFile && (
          <div className="flex gap-4 items-center pt-2 border-t">
            {oauthQuery.isLoading ? (
              <CircularProgress size={24} />
            ) : oauthQuery.data?.authorized ? (
              <>
                <div className="flex items-center gap-2">
                  <Check className="text-green-500" />
                  <div>
                    <p className="text-sm font-medium">Authorized</p>
                  </div>
                </div>
                {/*<Button*/}
                {/*  variant="outlined"*/}
                {/*  color="error"*/}
                {/*  size="small"*/}
                {/*  disabled={revokeMutation.isPending}*/}
                {/*  onClick={() => revokeMutation.mutate()}*/}
                {/*>*/}
                {/*  Revoke Access*/}
                {/*</Button>*/}
              </>
            ) : (
              oauthQuery.data && (
                <Button
                  variant="contained"
                  onClick={() => {
                    handleAuthorize(oauthQuery.data.clientId!);
                  }}
                  disabled={!oauthQuery.data.clientId}
                >
                  Authorize App Distribution Access
                </Button>
              )
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default FirebaseAppDistributionIntegration;
